// A ÁREA de uma linha do quadro de itens (05/10/2026): Área, Sub-área (quando a
// área tem) e o código de serviço da NF (só em item livre — na linha de serviço
// o código é o do catálogo). Usado na coluna "Área" da proposta/venda e no
// início do contrato, para os itens que chegaram lá sem área.
//
// A regra do que vale e do que falta não mora aqui: é `areaDoItem.ts`.
import { useState } from 'react';
import { Button, Select } from 'antd';
import {
  OPCOES_AREA, casaBusca, exigeSubarea, subareasDaArea, type Subarea,
} from '../../utils/areasEmpresa';
import { CNAES, OPCOES_CNAE, cnaeValido } from '../../utils/cnaes';
import {
  areaEfetivaDoItem, aoTrocarAreaDoItem, aoTrocarCnaeDoItem, aoTrocarSubareaDoItem, cnaeSugeridoDoItem, faltaNoItem,
  precisaDeArea, type ItemVenda, type ServicoDoCatalogo,
} from './areaDoItem';

const filtro = (digitado: string, opcao?: { label?: unknown }) => casaBusca(digitado, opcao?.label);
const descricaoDoCnae = (codigo?: string | null) => CNAES.find(c => c.codigo === codigo)?.descricao || '';

export function AreaDoItem({ item, servico, subareas, numero, onChange }: {
  item: ItemVenda;
  servico: ServicoDoCatalogo | null;
  subareas: Subarea[] | null;                 // null = a lista ainda não chegou
  numero: number;                             // posição da linha no quadro (1, 2, 3…)
  onChange: (novo: ItemVenda) => void;
}) {
  const ef = areaEfetivaDoItem(item, servico);
  const falta = faltaNoItem(item, servico, subareas);
  const precisa = precisaDeArea(item);
  const opcoesSub = subareasDaArea(subareas || [], ef.setor, ef.subarea_id)
    .map(s => ({ value: s.id, label: s.ativo ? s.nome : `${s.nome} (desativada)` }));
  // Na linha de serviço o código é o do catálogo. Em item livre (ou serviço
  // ainda sem código) aparece aqui: sem escolha, mostra o padrão da área como
  // SUGESTÃO (não é gravado — vira pendência do contrato para alguém conferir);
  // escolhido aqui, vale.
  const mostraCnae = !!ef.setor && !cnaeValido(servico?.cnae);
  const cnaeSugerido = cnaeSugeridoDoItem(item, servico);
  const segundaLinha = opcoesSub.length > 0 || mostraCnae;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Select
        size="small" aria-label={`Área do item ${numero}`}
        value={ef.setor || undefined}
        onChange={(v) => onChange(aoTrocarAreaDoItem(item, servico, v || null))}
        options={OPCOES_AREA}
        placeholder={precisa ? 'Escolha a área' : 'Não precisa de área'}
        showSearch filterOption={filtro}
        status={falta === 'area' ? 'error' : undefined}
        popupMatchSelectWidth={false}
        style={{ width: '100%' }}
      />
      {segundaLinha && (
        <div style={{ display: 'flex', gap: 3, minWidth: 0 }}>
          {opcoesSub.length > 0 && (
            <Select
              size="small" aria-label={`Sub-área do item ${numero}`}
              value={ef.subarea_id || undefined}
              onChange={(v) => onChange(aoTrocarSubareaDoItem(item, servico, v || null))}
              options={opcoesSub}
              placeholder={falta === 'subarea' ? 'Escolha a sub-área' : 'Sub-área (opcional)'}
              showSearch filterOption={filtro}
              // Linha que segue um serviço com sub-área: "limpar" não limparia (a
              // do serviço voltaria a valer). Dá para trocar, não para apagar.
              allowClear={!(ef.segue_servico && !!servico?.subarea_id)}
              status={falta === 'subarea' || falta === 'subarea_invalida' ? 'error' : undefined}
              popupMatchSelectWidth={false}
              style={{ flex: 1, minWidth: 0 }}
            />
          )}
          {mostraCnae && (
            <Select
              size="small" variant="borderless" aria-label={`Código de serviço da NF do item ${numero}`}
              title={ef.cnae
                ? `Código de serviço da nota escolhido para este item: ${ef.cnae} — ${descricaoDoCnae(ef.cnae)}.`
                : `Código de serviço da nota. Sem escolha, fica só a sugestão do padrão da área${cnaeSugerido ? ` (${cnaeSugerido} — ${descricaoDoCnae(cnaeSugerido)})` : ''}, para conferir no contrato. Escolha aqui se este item for de outro código.`}
              value={ef.cnae || undefined}
              onChange={(v) => onChange(aoTrocarCnaeDoItem(item, servico, v || null))}
              options={OPCOES_CNAE}
              placeholder={cnaeSugerido ? `NF ${cnaeSugerido}?` : 'NF'}
              labelRender={(o) => <span style={{ color: '#444', fontSize: 11 }}>NF {String(o.value)}</span>}
              showSearch filterOption={filtro} allowClear
              popupMatchSelectWidth={320}
              style={{ flex: opcoesSub.length > 0 ? '0 0 104px' : 1, minWidth: 0 }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// "Mesma área para todas as linhas que faltam" — uma proposta chega a ter 40
// linhas livres do mesmo serviço. A pessoa escolhe uma vez e aplica.
export function AreasEmLote({ quantas, soArea, subareas, onAplicar, semContagem }: {
  quantas: number;                            // linhas com área ou sub-área faltando
  soArea: boolean;                            // em todas falta a ÁREA (nenhuma é só sub-área)
  semContagem?: boolean;                      // quem usa já disse quantas faltam (não repete o número)
  subareas: Subarea[] | null;
  onAplicar: (escolha: { setor_chave: string; subarea_id: string | null }) => void;
}) {
  const [area, setArea] = useState<string | null>(null);
  const [sub, setSub] = useState<string | null>(null);
  const opcoesSub = subareasDaArea(subareas || [], area).map(s => ({ value: s.id, label: s.nome }));
  const faltaSub = exigeSubarea(subareas || [], area) && !sub;
  // O botão apagado diz o que falta (o `title` não existe no celular).
  const oQueFalta = !area ? 'Escolha a área.' : faltaSub ? 'Escolha também a sub-área.' : null;
  return (
    <div role="alert" style={{
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8, padding: '8px 10px',
      background: '#FFF8E8', border: '0.5px solid #E3C77A', borderRadius: 5, fontSize: 12, color: '#7A5200',
    }}>
      {semContagem ? <span>A mesma área para todas as que faltam:</span> : (
        <span>
          <strong>{quantas === 1 ? '1 item' : `${quantas} itens`} sem {soArea ? 'área' : 'área ou sub-área'}.</strong>{' '}
          Escolha na coluna Área de cada linha{quantas > 1 ? ' — ou a mesma para todas:' : ', ou aqui:'}
        </span>
      )}
      <Select
        size="small" aria-label="Área para as linhas que faltam" value={area || undefined}
        onChange={(v) => { setArea(v || null); setSub(null); }}
        options={OPCOES_AREA} placeholder="Área" showSearch filterOption={filtro} allowClear
        popupMatchSelectWidth={false} style={{ width: 210 }}
      />
      {opcoesSub.length > 0 && (
        <Select
          size="small" aria-label="Sub-área para as linhas que faltam" value={sub || undefined}
          onChange={(v) => setSub(v || null)}
          options={opcoesSub} placeholder="Sub-área" showSearch filterOption={filtro} allowClear
          status={faltaSub ? 'warning' : undefined}
          popupMatchSelectWidth={false} style={{ width: 190 }}
        />
      )}
      {/* Sem repetir a contagem: ela já está escrita no começo da faixa (ou em quem a usa). */}
      <Button size="small" type="primary" disabled={!!oQueFalta}
        onClick={() => { if (area) onAplicar({ setor_chave: area, subarea_id: sub }); }}>
        {quantas === 1 ? 'Aplicar' : 'Aplicar às que faltam'}
      </Button>
      {area && oQueFalta && <span style={{ fontSize: 11 }}>{oQueFalta}</span>}
    </div>
  );
}
